import {
  PropsWithChildren,
  ReactNode,
  createContext,
  useEffect,
  useState,
} from 'react';
import { ScrollView, View } from 'react-native';
import { Text } from '@/components/Text';
import Modal from 'react-native-modal/dist/modal';
import { Modal as TherapistModal } from '@/components/ui/therapist/Modal';
import { PrimaryButton } from '../../../Button';
import {
  DialogContextValue,
  DialogOptions,
  DialogProps,
  ShowDialogFn,
} from './types';
import { isPatientApp } from '@/config/constants';
import { Button } from '@/components/ui/therapist/Button';
import { useOverlayGuard } from '@/hooks/useOverlayGuard';
import { allowNativeDriver } from '@/utils/allowNativeDriver';

export const DialogContext = createContext<DialogContextValue | null>(null);
export function DialogProvider({ children }: PropsWithChildren) {
  const [dialogs, setDialogs] = useState<DialogOptions[]>([]);

  const removeDialog = () => {
    setDialogs((p) => p.slice(0, -1));
  };

  const removeAllDialogs = () => {
    setDialogs([]);
  };

  const showDialog: ShowDialogFn = (options: DialogOptions) => {
    setDialogs((p) => [...p, { ...options, id: Math.random() }]);
  };

  const currentDialogOptions = dialogs[dialogs.length - 1];

  return (
    <DialogContext.Provider
      value={{ showDialog, removeDialog, removeAllDialogs }}
    >
      {children}
      {!!currentDialogOptions && (
        <Dialog
          key={currentDialogOptions.id}
          isVisible
          options={currentDialogOptions}
          onClose={removeDialog}
        />
      )}
    </DialogContext.Provider>
  );
}

function Dialog({ isVisible, onClose, ...props }: DialogProps) {
  const [options, setOptions] = useState(props.options);
  useOverlayGuard(isVisible);
  const {
    danger = false,
    content,
    contentComponent,
    title,
    cancelOnBackdropPress,
    confirmAction,
    cancelAction,
    cancelable = true,
    headingComponent,
    hideCloseButton,
  } = options ?? {};

  function handlePressBackdrop() {
    if (!cancelOnBackdropPress) {
      return;
    }
    onClose?.();
  }

  const classNames = {
    header: {
      neutral: 'font-bold text-xl p-4 bg-neutral-100',
      danger: 'font-bold text-xl p-4 bg-red-500 text-white',
    },
  };

  useEffect(() => {
    setOptions(props.options);
  }, [props.options]);

  if (!isPatientApp) {
    return (
      <TherapistModal
        title={title ?? ''}
        isVisible={isVisible}
        onClose={hideCloseButton ? undefined : onClose}
        cancelActionButton={
          cancelAction ? (
            <Button
              look="secondary"
              onPress={() => cancelAction.onPress(undefined, onClose)}
              label={cancelAction.label}
            />
          ) : undefined
        }
        confirmActionButton={
          <Button
            onPress={() =>
              confirmAction?.onPress(undefined, onClose) ?? onClose?.()
            }
            label={confirmAction?.label ?? 'Ok'}
          />
        }
      >
        {content ? (
          <Text className="text-xl">{content}</Text>
        ) : contentComponent ? (
          contentComponent({ setOptions })
        ) : null}
      </TherapistModal>
    );
  }

  return (
    <Modal
      isVisible={isVisible}
      statusBarTranslucent
      onBackdropPress={handlePressBackdrop}
      className="flex-row items-center justify-center "
      useNativeDriver={allowNativeDriver()}
      useNativeDriverForBackdrop={allowNativeDriver()}
      hideModalContentWhileAnimating
      avoidKeyboard
    >
      <View className="bg-white w-full m-auto max-w-screen-md rounded-xl overflow-hidden">
        {!!headingComponent && headingComponent}
        {!!title && (
          <Text className={classNames.header[danger ? 'danger' : 'neutral']}>
            {title}
          </Text>
        )}
        <ScrollView className={!!contentComponent ? 'h-fit max-h-96' : ''}>
          {!!contentComponent && contentComponent({ setOptions })}
          {!contentComponent && !!content && (
            <View className="p-4">
              {typeof content === 'string' ? (
                <Text className="text-xl">{content}</Text>
              ) : (
                content
              )}
            </View>
          )}
        </ScrollView>

        <View className="p-4 flex-row-reverse justify-between">
          {!confirmAction && !cancelAction && (
            <PrimaryButton
              look={danger ? 'danger' : 'default'}
              label="OK"
              onPress={onClose}
            />
          )}
          {confirmAction ? (
            <PrimaryButton
              testID="confirm-modal"
              look={danger ? 'danger' : 'default'}
              {...confirmAction}
              onPress={(e) => confirmAction.onPress(e, onClose)}
            />
          ) : null}
          {cancelAction && cancelable ? (
            <PrimaryButton
              look={'neutral'}
              label={cancelAction.label}
              onPress={(e) => cancelAction.onPress(e, onClose)}
            />
          ) : null}
          {!cancelAction && cancelable ? (
            <PrimaryButton
              look={'neutral'}
              onPress={onClose}
              label="Abbrechen"
            />
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

interface ConfirmDialogProps {
  isVisible?: boolean;
  headerText?: string;
  contentText?: string;
  cancelAction?: () => void;
  cancelText?: string;
  confirmAction?: () => void;
  confirmText?: string;
  children?: ReactNode;
  redBackgroundConfirmButton?: boolean;
}

export function ConfirmDialog({
  isVisible,
  headerText,
  contentText,
  cancelAction,
  cancelText,
  confirmAction,
  confirmText,
  children,
  redBackgroundConfirmButton,
}: ConfirmDialogProps) {
  return (
    <Dialog
      isVisible={isVisible || false}
      onClose={cancelAction}
      options={{
        cancelable: typeof cancelAction === 'function',
        title: headerText,
        content: contentText,
        danger: !!redBackgroundConfirmButton,
        contentComponent: !!children ? () => children : undefined,
        cancelAction: cancelAction
          ? {
              label: cancelText ?? 'Abbrechen',
              onPress: cancelAction,
            }
          : undefined,
        confirmAction: confirmAction
          ? {
              label: confirmText ?? 'Ok',
              onPress: confirmAction,
            }
          : undefined,
      }}
    />
  );
}
